import { Component, computed, inject, signal } from '@angular/core';
import { BackButton, TranslatePipe } from '@cmi/addin-core';
import { Button, LoadingSpinner, Notification } from '@cmi/ui';
import * as wordControls from '../../../lib/word-content-controls';
import { FeatureToggleService } from '../../shared/feature-toggle.service';
import { SelectorProviderService } from '../../shared/selector-provider.service';
import { HomeService } from '../home.service';

@Component({
  selector: 'cmi-reports',
  imports: [TranslatePipe, BackButton, Button, Notification, LoadingSpinner],
  templateUrl: './reports.html',
  host: { class: 'flex flex-col h-full' }
})
export class Reports {
  private readonly _homeService = inject(HomeService);
  private readonly _selectorProviderService = inject(SelectorProviderService);
  private readonly _featureToggleService = inject(FeatureToggleService);

  protected readonly selectorProvidersSupported = computed(() =>
    this._featureToggleService.supportsSelectorProviders()
  );

  protected readonly value = signal('');
  protected readonly isSaving = signal(false);
  protected readonly duplicateError = signal(false);
  protected readonly saveSuccess = signal(false);
  protected readonly saveError = signal(false);
  protected readonly selectedProvider = signal('');
  protected readonly selectorProviders = computed(() => this._selectorProviderService.providers());
  protected readonly selectorProvidersLoading = computed(() =>
    this._selectorProviderService.isLoading()
  );
  protected readonly selectorProvidersError = computed(() => this._selectorProviderService.error());
  protected readonly externalLink = computed(
    () => this._homeService.reportsAppDetails()?.externalLink ?? ''
  );

  protected onOpenExternalLink(): void {
    window.open(this.externalLink(), '_blank', 'noopener,noreferrer');
  }

  protected onInput(event: Event): void {
    this.duplicateError.set(false);
    this.saveSuccess.set(false);
    this.saveError.set(false);
    this.value.set((event.target as HTMLInputElement).value);
  }

  protected onProviderChange(event: Event): void {
    this.selectedProvider.set((event.target as HTMLSelectElement).value);
  }

  protected async onSave(): Promise<void> {
    const val = this.value();
    if (!val) {
      return;
    }

    const reportName = val.split('\\').filter(Boolean).at(-1) ?? val;
    const title = `CMR_${reportName}`;

    this.isSaving.set(true);
    this.duplicateError.set(false);
    this.saveError.set(false);
    try {
      const existing = await wordControls.getAllContentControls();
      if (existing.some((control) => control.title === title)) {
        this.duplicateError.set(true);
        return;
      }

      const providerKey = this.selectedProvider();
      const selectorKey = providerKey || undefined;

      await wordControls.insertRichTextControl({
        title,
        tag: { path: val, selector: selectorKey },
        placeholder: reportName
      });

      if (providerKey && selectorKey) {
        await wordControls.writeProperty(`Selector_${selectorKey}`, '-');
        await wordControls.writeProperty(`Selector_${selectorKey}.Key`, providerKey);
      }

      this.value.set('');
      this.selectedProvider.set('');
      this.saveSuccess.set(true);
    } catch {
      this.saveError.set(true);
    } finally {
      this.isSaving.set(false);
    }
  }

  protected reloadSelectorProviders(): void {
    this._selectorProviderService.reload();
  }
}
